import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection, getDocs, addDoc, updateDoc,
  doc, Timestamp, query, orderBy
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: '#2a6041', label: 'Published' },
    draft: { bg: '#666', label: 'Draft' },
    scheduled: { bg: '#1a4f8a', label: 'Scheduled' },
    deleted: { bg: '#ccc', color: '#666', label: 'Deleted' }
  };
  const s = map[status] || map.published;
  return (
    <span style={{
      background: s.bg, color: s.color || 'white',
      fontSize: '0.68rem', padding: '2px 8px', borderRadius: '10px',
      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
};

const AdminResources = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('published');
  const [uploadScheduledAt, setUploadScheduledAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchResources().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResources = async () => {
    const snap = await getDocs(query(collection(db, 'resources'), orderBy('order', 'asc')));
    setResources(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleUpload = async () => {
    if (!title.trim()) { setSaveMsg('Title is required.'); return; }
    if (!file) { setSaveMsg('Please select a PDF file.'); return; }
    if (uploadStatus === 'scheduled' && !uploadScheduledAt) { setSaveMsg('Publish date/time is required for scheduled status.'); return; }
    setSaving(true);
    setUploading(true);
    setSaveMsg('');

    try {
      const storageRef = ref(storage, `resources/${Date.now()}_${file.name}`);
      const task = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
          reject,
          resolve
        );
      });

      const url = await getDownloadURL(task.snapshot.ref);
      const minOrder = resources.filter(r => r.status !== 'deleted').reduce((min, r) => Math.min(min, r.order ?? 0), 0);

      await addDoc(collection(db, 'resources'), {
        title: title.trim(),
        url,
        order: minOrder - 1,
        status: uploadStatus,
        scheduled_at: uploadStatus === 'scheduled' && uploadScheduledAt
          ? Timestamp.fromDate(new Date(uploadScheduledAt))
          : null,
        created_at: Timestamp.fromDate(new Date())
      });

      setTitle('');
      setFile(null);
      setUploadProgress(0);
      setUploadStatus('published');
      setUploadScheduledAt('');
      setSaveMsg('Uploaded!');
      await fetchResources();
    } catch (err) {
      console.error(err);
      setSaveMsg('Upload failed. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleRename = async (id) => {
    if (!editTitle.trim()) return;
    await updateDoc(doc(db, 'resources', id), { title: editTitle.trim() });
    setEditingId(null);
    setEditTitle('');
    await fetchResources();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource? It will be hidden from the website.')) return;
    await updateDoc(doc(db, 'resources', id), { status: 'deleted' });
    await fetchResources();
  };

  const handleRestore = async (id) => {
    await updateDoc(doc(db, 'resources', id), { status: 'published' });
    await fetchResources();
  };

  const moveResource = async (index, direction) => {
    const visible = resources.filter(r => r.status !== 'deleted');
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= visible.length) return;
    const a = visible[index];
    const b = visible[swapIndex];
    await Promise.all([
      updateDoc(doc(db, 'resources', a.id), { order: b.order ?? swapIndex }),
      updateDoc(doc(db, 'resources', b.id), { order: a.order ?? index })
    ]);
    await fetchResources();
  };

  const effectiveStatus = (item) => {
    if (item.status === 'scheduled' && item.scheduled_at) {
      const sat = item.scheduled_at.toDate ? item.scheduled_at.toDate() : new Date(item.scheduled_at);
      if (sat <= new Date()) return 'published';
    }
    return item.status || 'published';
  };

  const visibleResources = showDeleted ? resources : resources.filter(r => r.status !== 'deleted');

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <h1 className="stt-admin-title">Resources</h1>
      <hr />

      {/* Resources list */}
      <div className="stt-admin-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <label style={{ fontSize: '0.82rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
        </div>
        {visibleResources.length === 0 ? (
          <p style={{ color: '#888' }}>No resources yet.</p>
        ) : (
          <div className="stt-admin-list">
            {visibleResources.map((r, i) => (
              <div key={r.id} className={`stt-admin-list-row${r.status === 'deleted' ? ' deleted' : ''}`}>
                <div className="stt-admin-list-info">
                  {editingId === r.id ? (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(r.id); if (e.key === 'Escape') setEditingId(null); }}
                        style={{ maxWidth: '280px' }}
                        autoFocus
                      />
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRename(r.id)}>Save</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="stt-admin-list-title">
                        <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
                      </div>
                      <div className="stt-admin-list-meta">
                        <StatusBadge status={effectiveStatus(r)} />
                      </div>
                    </>
                  )}
                </div>
                <div className="stt-admin-list-actions">
                  {r.status !== 'deleted' && (
                    <>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => moveResource(i, -1)} disabled={i === 0}>↑</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => moveResource(i, 1)} disabled={i === visibleResources.length - 1}>↓</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditingId(r.id); setEditTitle(r.title); }}>Rename</button>
                    </>
                  )}
                  {r.status === 'deleted' ? (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(r.id)}>Restore</button>
                  ) : (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload form */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>Upload New Resource</h2>

        <div className="mb-3">
          <label className="stt-form-label">Title *</label>
          <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Sacraments" />
        </div>

        <div className="mb-3">
          <label className="stt-form-label">PDF File *</label>
          <input
            type="file"
            accept=".pdf,application/pdf"
            className="form-control"
            onChange={e => setFile(e.target.files[0])}
            disabled={uploading}
          />
          {uploading && <p className="stt-upload-progress">Uploading… {uploadProgress}%</p>}
        </div>

        <div className="row mb-3">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <label className="stt-form-label">Status</label>
            <select className="form-control" value={uploadStatus} onChange={e => setUploadStatus(e.target.value)}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>
          {uploadStatus === 'scheduled' && (
            <div className="col-12 col-md-6">
              <label className="stt-form-label">Publish Date &amp; Time *</label>
              <input
                type="datetime-local"
                className="form-control"
                value={uploadScheduledAt}
                onChange={e => setUploadScheduledAt(e.target.value)}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn stt-btn-primary" onClick={handleUpload} disabled={saving}>
            {saving ? 'Uploading...' : 'Upload Resource'}
          </button>
          {saveMsg && (
            <span className={`stt-save-message ${saveMsg === 'Uploaded!' ? 'success' : 'error'}`}>
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResources;
